import React from 'react';
import { motion, Variants } from 'framer-motion';
import { Briefcase, Users, Armchair, Handshake, Shield } from 'lucide-react';
import { CommunityPresence, LivingInAreaData } from '../../types/livingInArea';

interface CommunitySectionProps {
  data: LivingInAreaData['communityProfile'];
}

// --- Helper Functions and Data Mapping ---

// Maps presence string to a corresponding color class
const getPresenceColor = (presence: string): { text: string; bg: string } => {
  const normalized = presence.toLowerCase();
  if (normalized.includes('high') || normalized.includes('dominant')) {
    return {
      text: 'text-green-700',
      bg: 'bg-green-100'
    };
  }
  if (normalized.includes('medium') || normalized.includes('moderate')) {
    return {
      text: 'text-yellow-700',
      bg: 'bg-yellow-100'
    };
  }
  return {
    text: 'text-slate-600',
    bg: 'bg-slate-100'
  };
};

const profiles = [
  {
    label: 'Young Professionals',
    data: 'youngProfessionals',
    icon: <Briefcase size={28} />,
    gradient: 'from-blue-500 to-cyan-500' // Explicit gradient
  },
  {
    label: 'Families',
    data: 'families',
    icon: <Users size={28} />,
    gradient: 'from-teal-500 to-green-500' // Explicit gradient
  },
  {
    label: 'Retirees',
    data: 'retirees',
    icon: <Armchair size={28} />,
    gradient: 'from-purple-500 to-indigo-500' // Explicit gradient
  }
];

// --- Sub-Components ---

const CommunityCard: React.FC<{
  label: string;
  data: CommunityPresence;
  icon: React.ReactNode;
  gradient: string;
}> = ({ label, data, icon, gradient }) => {
  const { text: presenceTextColor } = getPresenceColor(data.presence);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.4 }}
      whileHover={{
        y: -8,
        boxShadow:
          '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
      }}
      // Explicit colors for card
      className="group rounded-[2rem] border border-slate-200 bg-white p-8 text-center shadow-xl transition-transform"
    >
      <div
        className={`mb-6 mx-auto flex h-16 w-16 items-center justify-center rounded-2xl bg-gradient-to-br ${gradient} text-white shadow-lg transition-transform duration-300 group-hover:scale-110`}
      >
        {icon}
      </div>

      <div
        className={`mb-2 text-xs font-black uppercase tracking-[0.15em] ${presenceTextColor}`}
      >
        {data.presence} Presence
      </div>
      <h3 className="mb-4 text-xl font-bold text-slate-900">
        {label}
      </h3>
      <p className="text-sm italic leading-relaxed text-slate-700">
        "{data.why}"
      </p>
    </motion.div>
  );
};

// --- Main Component ---

export const CommunitySection: React.FC<CommunitySectionProps> = ({ data }) => {
  return (
    <section className="max-w-7xl mx-auto px-4 space-y-12">
      {/* Header */}
      <div className="text-center">
        <h2 className="mb-2 text-4xl font-extrabold text-gray-900 dark:text-white md:text-5xl">
          Who Lives Here?
        </h2>
        <p className="text-lg text-slate-600">
          Community demographics and character
        </p>
      </div>

      {/* Profile Cards */}
      <div className="grid gap-8 md:grid-cols-3">
        {profiles.map((profile) => (
          <CommunityCard
            key={profile.label}
            label={profile.label}
            // Accessing data dynamically using the string key
            data={data[profile.data as keyof typeof data] as CommunityPresence}
            icon={profile.icon}
            gradient={profile.gradient}
          />
        ))}
      </div>

      {/* Community Insights */}
      {(data.communityStability || data.neighbourInteraction) && (
        <div className="grid gap-8 md:grid-cols-2 pt-6">
          {data.communityStability && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.8 }}
              transition={{ delay: 0.1 }}
              whileHover={{ scale: 1.01 }}
              // Explicit colors
              className="flex min-h-[170px] items-start gap-4 rounded-3xl border border-slate-200 bg-white p-6 shadow-md transition-transform"
            >
              <div className="rounded-2xl bg-blue-500/10 p-3 text-blue-600">
                <Shield size={24} />
              </div>
              <div>
                <p className="mb-1 text-xs font-bold uppercase tracking-wider text-slate-500">
                  Community Stability
                </p>
                <p className="text-xl font-extrabold text-slate-900">
                  {data.communityStability || 'Not enough local signal yet'}
                </p>
              </div>
            </motion.div>
          )}

          {data.neighbourInteraction && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.8 }}
              transition={{ delay: 0.2 }}
              whileHover={{ scale: 1.01 }}
              // Explicit colors
              className="flex min-h-[170px] items-start gap-4 rounded-3xl border border-slate-200 bg-white p-6 shadow-md transition-transform"
            >
              <div className="rounded-2xl bg-teal-500/10 p-3 text-teal-600">
                <Handshake size={24} />
              </div>
              <div>
                <p className="mb-1 text-xs font-bold uppercase tracking-wider text-slate-500">
                  Neighbour Interaction
                </p>
                <p className="text-xl font-extrabold text-slate-900">
                  {data.neighbourInteraction || 'Not enough local signal yet'}
                </p>
              </div>
            </motion.div>
          )}
        </div>
      )}
    </section>
  );
};
